import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { reimputeActivities } from "../api/activities";
import type { TimeDay, UnassignedActivity } from "../api/types";
import {
  AUTO_REASONS,
  batchCorrelationId,
  planAutoAttach,
} from "../lib/autoAttach";
import { formatMinutes } from "../lib/format";
import { useUiStore } from "../state/uiStore";
import { invalidateReimputationReads } from "./useActivities";

/** Créneaux déjà tentés dans cet onglet : un remontage, une relecture ou un
 * échec ne relancent pas leur réimputation. */
const attempted = new Set<string>();

export function resetAutoAttachForTests() {
  attempted.clear();
}

/**
 * Rattache de lui-même le temps sans projet que Chronos sait deviner, dès que
 * l'écran le lit (Ma journée, feuille de semaine). TAWA suggère, Chronos
 * décide : chaque lot passe par la réimputation de TAWA avec un motif qui dit
 * pourquoi. `enabled` : le salarié regarde son propre temps, feuille en
 * brouillon ou pas encore générée.
 */
export function useAutoAttach(
  activities: UnassignedActivity[] | undefined,
  days: TimeDay[],
  enabled: boolean,
  where = "dans la frise",
) {
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);
  const daysKey = days
    .map(
      (d) => `${d.date}:${d.blocks.map((b) => b.project_id ?? "").join("/")}`,
    )
    .join(",");

  useEffect(() => {
    if (!enabled || !activities?.length) return;
    const batches = planAutoAttach(activities, days, attempted);
    if (batches.length === 0) return;
    for (const b of batches) for (const i of b.items) attempted.add(i.event_id);
    void (async () => {
      let minutes = 0;
      for (const b of batches) {
        try {
          await reimputeActivities({
            reason: AUTO_REASONS[b.kind],
            correlation_id: batchCorrelationId(b),
            items: b.items,
          });
          minutes += b.minutes;
        } catch {
          // Le lot reste au salarié, sans nouvel essai.
        }
      }
      if (minutes === 0) return;
      invalidateReimputationReads(qc);
      pushToast({
        variant: "info",
        title: `${formatMinutes(minutes)} rattachées automatiquement`,
        subtitle: `Marquées « auto » ${where} : un clic pour changer.`,
      });
    })();
    // `days` est rebâti à chaque rendu : daysKey dit quand il change vraiment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, enabled, daysKey]);
}
